import {CopilotNavigationWithContext} from '@github-ui/copilot-chat/components/navigation/CopilotNavigation'
import {useChatManager} from '@github-ui/copilot-chat/CopilotChatManagerContext'
import {copilotFeatureFlags} from '@github-ui/copilot-chat/utils/copilot-feature-flags'
import {AgentsList} from '@github-ui/dashboard-lists/components/AgentsList'
import {AgentsTaskList} from './AgentsTaskList'
import {setTitle} from '@github-ui/document-metadata'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {sendEvent} from '@github-ui/hydro-analytics'
import type {IntentDetectionMode} from '../../types/intent-detection-mode'
import {TransactionalMessageBanner} from '@github-ui/in-product-targeting'
import {ContentView} from '@github-ui/pacer/ContentView'
import {Layout as PacerLayout} from '@github-ui/pacer/Layout'
/* eslint-disable-next-line @github-ui/github-monorepo/no-use-app-payload --
   useAppPayload is deprecated. See
   https://thehub.github.com/epd/engineering/dev-practicals/frontend/react/data-router/migration/app-payload-deprecation/ */
import {useAppPayload} from '@github-ui/react-core/use-app-payload'
import {useSearchParams} from '@github-ui/use-navigate'
import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {ConsecutiveCAPI401sProvider} from '../../contexts/ConsecutiveCAPI401sContext'
import {CurrentUserProvider} from '../../contexts/CurrentUserContext'
import {useSSOChangeDetection} from '../../hooks/use-sso-change-detection'
import type {DropdownRepository} from '../../hooks/useCopilotRepoFetching'
import {useCreateAPITask} from '../../hooks/use-create-api-task'
import {useCreateLocalDeviceSession} from '../../hooks/use-create-local-device-session'
import type {CreateAPITaskMutationParams} from '../../types/api-task'
import {useTaskEntrypoint} from '../../hooks/useTaskEntrypoint'
import {useAgentModels} from '../../hooks/useAgentModels'
import {
  getAgentTasksQueryKeyForMutation,
  getFlatTaskList,
  setFlatTaskList,
  PAGE_SIZE,
  type FetchTasksResponse,
} from '../../hooks/useTasks'
import type {AgentTasksAppPayload} from '../../types/agent-tasks-app-payload'
import type {SelectedAgent} from '../../types/session'
import {NewTaskChatInput} from '../new-task-chat-input/NewTaskChatInput'
import {
  TaskCreationFailedBannerList,
  type FailedAPITaskCreationAttempt,
  getAPITaskParamsFromAttempt,
} from '../TaskCreationFailedBanner'
import {CopilotDisclosure} from '../CopilotDisclosure'
import {AgentsBlankslate} from './AgentsBlankslate'
import {AgentIcebreakers} from './AgentIcebreakers'
import {useInitializeAuthToken} from '../../hooks/use-initialize-auth-token'
import {useIcebreakers} from '../../hooks/use-icebreakers'
import {useRepoTasksFilter} from '../../hooks/use-repo-tasks-filter'
import {RepoFilterPicker} from './RepoFilterPicker'
import styles from './AgentsView.module.css'

export function AgentsViewWrapper() {
  const {agentsEnabled, hasCEorCBAccess, memberRequestEntities, initialAuthToken, currentUser} =
    useAppPayload<AgentTasksAppPayload>()

  // Initialize auth token from SSR payload before any CAPI requests are made
  useInitializeAuthToken(initialAuthToken)

  return (
    <CurrentUserProvider currentUser={currentUser} currentUserIsSessionOwner>
      <ConsecutiveCAPI401sProvider>
        <PacerLayout data-testid="agents-layout">
          <PacerLayout.Sidebar>
            <CopilotNavigationWithContext />
          </PacerLayout.Sidebar>

          <PacerLayout.ColumnLeft>
            {!agentsEnabled ? (
              <AgentsBlankslate hasCEorCBAccess={hasCEorCBAccess} memberRequestEntities={memberRequestEntities} />
            ) : (
              <AgentsView />
            )}
          </PacerLayout.ColumnLeft>
        </PacerLayout>
      </ConsecutiveCAPI401sProvider>
    </CurrentUserProvider>
  )
}

export function AgentsView() {
  const [activeTab, setActiveTab] = useState<'active' | 'completed'>('active')
  const [searchParams, setSearchParams] = useSearchParams()
  const {initialAuthToken, transactionalMessageBanner, icebreakerVariant, agentQuotaSnapshot} =
    useAppPayload<AgentTasksAppPayload>()

  // Initialize auth token from SSR payload to avoid double token fetching
  useInitializeAuthToken(initialAuthToken)

  const takenActionOnAgentsViewRef = useRef(false)

  const [failedTaskCreationAttempts, setFailedTaskCreationAttempts] = useState<FailedAPITaskCreationAttempt[]>([])

  // Detect SSO changes and clear cached tasks
  useSSOChangeDetection()

  const {
    chatInputRef,
    textAreaRef,
    visible: icebreakersVisible,
    handleSelect: handleIcebreakerSelect,
    handleDismiss: handleIcebreakerDismiss,
    handleIcebreakerSubmit,
  } = useIcebreakers({location: 'agents_page', variant: icebreakerVariant})

  // Repository filter (persisted in URL search params)
  const {repoFilter, setRepoFilter} = useRepoTasksFilter()

  const handleCreateTaskError = useCallback((error: Error, variables: CreateAPITaskMutationParams) => {
    setFailedTaskCreationAttempts(prev => [
      ...prev,
      {
        ...variables,
        errorMessage: error.message,
        timestamp: new Date().toISOString(),
      },
    ])
  }, [])

  const taskListMutationConfig = useMemo(
    () => ({
      queryKey: getAgentTasksQueryKeyForMutation(),
      getFlatTaskList,
      setFlatTaskList,
      pageSize: PAGE_SIZE,
    }),
    [],
  )

  const {createTask} = useCreateAPITask<FetchTasksResponse>({
    ...taskListMutationConfig,
    onError: handleCreateTaskError,
  })
  const localDeviceMutation = useCreateLocalDeviceSession<FetchTasksResponse>({
    optimisticTaskListConfig: taskListMutationConfig,
  })
  const onLocalDeviceSubmit = localDeviceMutation.mutateAsync

  const manager = useChatManager()

  const {models: availableModels} = useAgentModels()

  useEffect(() => {
    // Set the title of the page
    setTitle('Agents · GitHub Copilot')

    // Log feature flag values when AgentsView first renders
    sendEvent('agent_sessions.task_view', {
      target: 'AGENTS_VIEW_LOADED',
      defaultToTask: copilotFeatureFlags.defaultToAgentInput,
      defaultToTaskControl: copilotFeatureFlags.defaultToAgentControl,
      askModeDropdown: copilotFeatureFlags.askModeDropdown,
    })

    // Log bounce event if user leaves without creating a task
    return () => {
      if (!takenActionOnAgentsViewRef.current) {
        sendEvent('dotcom_chat.activate', {
          target: 'AGENTS_BOUNCE_TO_CHAT',
          mode: 'immersive',
          bounceToUrl: document.location.href,
          defaultToTask: copilotFeatureFlags.defaultToAgentInput,
          defaultToTaskControl: copilotFeatureFlags.defaultToAgentControl,
        })
      }
    }
  }, [])

  useEffect(() => {
    // clear out the thread on mount
    manager.selectThread(null, {includeThreads: false, clearTopic: true})
  }, [manager])

  const handleUserSubmit = useCallback(
    async (
      content: string,
      selectedRepo: DropdownRepository | null,
      selectedBranch: string,
      selectedAgent: SelectedAgent,
      _selectedMode?: IntentDetectionMode,
      modelId?: string,
    ) => {
      takenActionOnAgentsViewRef.current = true
      handleIcebreakerSubmit(content)

      const trimmedContent = content.trim()
      if (trimmedContent === '' || !selectedBranch || selectedBranch.trim() === '') return
      if (selectedRepo) {
        // Reset to open tab and page 1 when creating a new task
        setActiveTab('active')
        searchParams.delete('completed')
        searchParams.delete('page')
        setSearchParams(searchParams)

        // Resolve model: use explicit selection, then is_chat_default, then first available
        const resolvedModelId = modelId ?? availableModels?.find(m => m.is_chat_default)?.id ?? availableModels?.[0]?.id

        // This mutation will automatically invalidate and refetch the tasks after completion
        createTask({
          nwo: selectedRepo.nameWithOwner,
          problemStatement: trimmedContent,
          baseRef: selectedBranch,
          eventType: 'agents_page',
          eventContent: trimmedContent,
          model: resolvedModelId,
          customAgent: selectedAgent?.subAgentSlug,
          agentId: selectedAgent?.topLevelAgentId,
          intentDetectionMode: 'task', // For now we explicitly don't use intent detection on this path
        })
      } else {
        manager.sendChatMessage({
          content: trimmedContent,
          thread: null,
          references: [],
        })
      }
    },
    [handleIcebreakerSubmit, searchParams, setSearchParams, createTask, manager, availableModels],
  )

  useTaskEntrypoint(handleUserSubmit)

  // TODO: Now that we have uuids on the request, we might be able to use that to correlate requests and responses more easily.
  const handleRetry = (attemptIndex: number) => {
    const attempt = failedTaskCreationAttempts[attemptIndex]
    if (attempt) {
      // Remove this specific attempt from the list
      setFailedTaskCreationAttempts(prev => prev.filter((_, index) => index !== attemptIndex))
      createTask(getAPITaskParamsFromAttempt(attempt))
    }
  }

  const title = 'Agents'
  const agentName = 'Copilot cloud agent'
  const description = `Delegate tasks to GitHub ${agentName} to work on in the background, and then monitor Copilot's progress.`

  return (
    <ContentView.Container noHeader width="small">
      {isFeatureEnabled('ipm_global_transactional_message_agents') && transactionalMessageBanner ? (
        <ContentView.Banner>
          <TransactionalMessageBanner banner={transactionalMessageBanner} />
        </ContentView.Banner>
      ) : null}
      <ContentView.Header>
        <ContentView.Title>{title}</ContentView.Title>
        <ContentView.Description>{description}</ContentView.Description>
      </ContentView.Header>
      <ContentView.Section>
        <NewTaskChatInput
          handleUserSubmit={handleUserSubmit}
          eventType="agents_page"
          chatInputRef={chatInputRef}
          textAreaRef={textAreaRef}
          onLocalDeviceSubmit={onLocalDeviceSubmit}
          agentQuotaSnapshot={agentQuotaSnapshot}
        />
        <TaskCreationFailedBannerList
          attempts={failedTaskCreationAttempts}
          onRetry={handleRetry}
          onDismiss={index => setFailedTaskCreationAttempts(prev => prev.filter((_, i) => i !== index))}
          className={styles.failedTaskBanners}
        />
      </ContentView.Section>
      {icebreakersVisible && (
        <ContentView.Section>
          <AgentIcebreakers
            onSelect={handleIcebreakerSelect}
            onDismiss={handleIcebreakerDismiss}
            location="agents_page"
            variant={icebreakerVariant}
          />
        </ContentView.Section>
      )}
      <ContentView.Section>
        {isFeatureEnabled('copilot_mission_control_agents_task_list') ? (
          <AgentsTaskList
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            repoFilter={isFeatureEnabled('copilot_mission_control_tasks_repo_filter') ? repoFilter : undefined}
            setRepoFilter={isFeatureEnabled('copilot_mission_control_tasks_repo_filter') ? setRepoFilter : undefined}
          />
        ) : (
          <AgentsList
            title="Recent sessions"
            initialResultCount={PAGE_SIZE}
            showPillMenu
            showPagination
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            includeCounts
            repoFilter={isFeatureEnabled('copilot_mission_control_tasks_repo_filter') ? repoFilter : undefined}
            additionalFilters={
              isFeatureEnabled('copilot_mission_control_tasks_repo_filter') ? (
                <RepoFilterPicker repoFilter={repoFilter} onFilterChange={setRepoFilter} />
              ) : undefined
            }
          />
        )}
      </ContentView.Section>

      <CopilotDisclosure />
    </ContentView.Container>
  )
}
